import $http from '@/core/services/http.service';
import { ActionTree } from 'vuex';

import { State } from '@/manager/client/store/state';
import Endpoints from '@/constants/endpoints';
import UtilsService from '@/core/services/utils.service';

export interface Actions {
  fetchClientList: {};
}

// Solo la última búsqueda puede escribir en el store: las respuestas llegan desordenadas.
let lastClientListRequest = 0;

export const actions: ActionTree<State, any> = {
  fetchClientList: ({ commit }, payload) => {
    const requestId = ++lastClientListRequest;
    return new Promise((resolve, reject) => {
      let params: any = {};
      params = UtilsService.getQueryParams(payload);

      $http
        .get(`${Endpoints.client}`, { params })
        .then((res) => {
          // ponytail: la respuesta obsoleta deja su promesa sin resolver; cancelar con axios si molesta.
          if (requestId !== lastClientListRequest) {
            return;
          }
          const data = res.data;
          commit('setClientList', data.results);
          delete res.data.results;

          commit('setClientPaginationInfo', data);

          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  fetchClient: ({ commit, getters }, payload) => {
    return new Promise((resolve, reject) => {
      const client = getters.getClient(payload.id);
      if (client) {
        resolve(client);
      } else {
        $http
          .get(`${Endpoints.client}${payload.id}/`)
          .then((res) => {
            const data = res.data;
            commit('newClient', data);
            resolve(data);
          })
          .catch((err) => {
            reject(err);
          });
      }
    });
  },
  newClient: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      $http
        .post(`${Endpoints.client}`, payload.newClient)
        .then((res) => {
          const data = res.data;
          commit('newClient', data);
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  addClientToStore: ({ commit }, payload) => {
    commit('newClient', payload);
  },
  updateClient: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      $http
        .put(`${Endpoints.client}${payload.id}/`, payload.newClient)
        .then((res) => {
          const data = res.data;
          commit('updateClient', data);
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  deleteClient: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      $http
        .delete(`${Endpoints.client}${payload.id}/`)
        .then((res) => {
          commit('deleteClient', payload);
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  patchClient: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      $http
        .patch(`${Endpoints.client}${payload.id}/`, payload.data)
        .then((res) => {
          commit('updateClient', res.data);
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  fetchAcquiredCoursesList: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      $http
        .get(Endpoints.acquiredCourses(payload.id))
        .then((res) => {
          commit('setAcquiredCoursesList', res.data);
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  fetchClientPayment: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      $http
        .get(Endpoints.clientPayment(payload.id, payload.viewStatus))
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  fetchClientPaymentDetail: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      $http
        .get(Endpoints.clientPaymentDetail(payload.id, payload.viewStatus))
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  newClientPayment: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      $http
        .post(Endpoints.clientPayment(payload.id, payload.viewStatus), payload)
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  updateClientPayment: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      $http
        .put(Endpoints.clientPayment(payload.id, payload.viewStatus), payload)
        .then((res) => {
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  fetchFiscalDataExtension: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      let params: any = {};
      params = UtilsService.getQueryParams(payload);
      $http
        .get(`${Endpoints.searchFiscalDataExtension()}`, {params})
        .then((res) => {
          commit('setSearchFiscalDataExtension', res.data);
          resolve(res.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  setAcquiredCoursesList: ({ commit }, payload) => {
    commit('setAcquiredCoursesList', payload);
  },
  addClientPaginationInfoOption: ({ commit }, payload) => {
    commit('addClientPaginationInfoOption', payload);
  },
  setSearchString: ({ commit }, payload) => {
    commit('setSearchString', payload);
  },
  setClientPaginationInfo: ({ commit }, payload) => {
    commit('setClientPaginationInfo', payload);
  },
  setGenderFilter: ({ commit }, payload) => {
    commit('setGenderFilter', payload);
  },
  setLocationFilter: ({ commit }, payload) => {
    commit('setLocationFilter', payload);
  },
  setFiscalDataFilter: ({ commit }, payload) => {
    commit('setFiscalDataFilter', payload);
  },
  setClientDateFilter: ({ commit }, payload) => {
    commit('setClientDateFilter', payload);
  },
  setProcedencyFilter: ({ commit }, payload) => {
    commit('setProcedencyFilter', payload);
  },
  setLevelUpDataFilter: ({ commit }, payload) => {
    commit('setLevelUpDataFilter', payload);
  },
  setCurseFilter: ({ commit }, payload) => {
    commit('setCurseFilter', payload);
  },
  setConferenceFilter: ({ commit }, payload) => {
    commit('setConferenceFilter', payload);
  },
  setEventsFilter: ({ commit }, payload) => {
    commit('setEventsFilter', payload);
  },
  setTagsFilter: ({ commit }, payload) => {
    commit('setTagsFilter', payload);
  },
  fetchProcedency: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      $http
        .get(Endpoints.procedency)
        .then((res) => {
          const data = res.data.results;
          commit('setProcedencyList', data);
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  sendClientListToMail: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      $http
        .get(`${Endpoints.clients}csv/${payload}`)
        .then((res) => {
          const data = res.data.results;
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  fetchClientTagList: ({ commit, getters }, payload) => {
    return new Promise((resolve, reject) => {
      $http
        .get(`${Endpoints.clientTag}`)
        .then((res) => {
          const data = res.data.results;
          commit('setTagList', data);
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  fetchClientCountryList: ({ commit, getters }, payload) => {
    return new Promise((resolve, reject) => {
      $http
        .get(`${Endpoints.country}`)
        .then((res) => {
          const data = res.data.results;
          commit('setCountryList', data);
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  fetchClientProvinceList: ({ commit, getters }, payload) => {
    return new Promise((resolve, reject) => {
      $http
        .get(`${Endpoints.province}`)
        .then((res) => {
          const data = res.data.results;
          commit('setProvinceList', data);
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  fetchClientPostalCodeList: ({ commit, getters }, payload) => {
    return new Promise((resolve, reject) => {
      $http
        .get(`${Endpoints.postalCode}`)
        // .get(`${Endpoints.postalCode}?search=${payload}`)
        .then((res) => {
          const data = res.data.results;
          commit('setProvinceList', data);
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  fetchTlpList: ({ commit, getters }, payload) => {
    return new Promise((resolve, reject) => {
      $http
        .get(`${Endpoints.tlp}`)
        .then((res) => {
          const data = res.data.results;
          commit('setTlpList', data);
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
};
